import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import LogFormShell from '../UI/LogFormShell';
import MedicationEditorSheet from '../Dashboard/shared/MedicationEditorSheet';
import {
  formatAdministrationTimeLabel,
  getMedicationAdministrationConfirmationQuestion,
  getAdministrationOptionLabel,
  MEDICATION_ADMINISTRATION_OUTCOMES,
} from '../../utils/medicationAdministrationReview';

const MedicationAdministrationReviewSheet = ({ controller, childName = '' }) => {
  if (!controller) return null;

  const scheduledOptions = controller.options.filter((option) => option.scheduledForDetectedTime);
  const normalizedSearch = controller.search.trim().toLowerCase();
  const visibleOptions = controller.options.filter((option) => (
    !normalizedSearch
    || getAdministrationOptionLabel(option).toLowerCase().includes(normalizedSearch)
  ));
  const selectedOption = controller.options.find((option) => controller.selectedIds.includes(option.id)) || null;
  const confirmationQuestion = selectedOption
    ? getMedicationAdministrationConfirmationQuestion({
        review: controller.review,
        option: selectedOption,
        status: controller.administrationStatus,
      })
    : '';

  return (
    <>
      {controller.open ? (
        <LogFormShell
          open
          onClose={controller.closeReview}
          title="Review medication"
          subtitle={`${childName || 'This person'} · ${formatAdministrationTimeLabel(controller.review)}`}
          mobileBreakpoint="md"
        >
          <Stack spacing={1.2}>
            <Typography sx={{ color: '#4B5563', fontSize: '0.88rem', lineHeight: 1.45 }}>
              {confirmationQuestion || controller.copy.instruction}
            </Typography>

            <Box>
              <Typography sx={{ mb: 0.55, color: '#4B5563', fontSize: '0.78rem', fontWeight: 800 }}>
                Final dose outcome
              </Typography>
              <ToggleButtonGroup
                exclusive
                value={controller.administrationStatus}
                onChange={(_event, value) => value && controller.setAdministrationStatus(value)}
                aria-label="Final dose outcome"
                sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.55, '& .MuiToggleButtonGroup-grouped': { borderRadius: '10px !important', border: '1px solid rgba(0,0,0,0.16) !important' } }}
              >
                {MEDICATION_ADMINISTRATION_OUTCOMES.map((outcome) => (
                  <ToggleButton key={outcome.value} value={outcome.value} size="small" aria-label={outcome.label}>
                    {outcome.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {controller.error ? <Alert severity="error">{controller.error}</Alert> : null}

            {scheduledOptions.length ? (
              <Button variant="outlined" onClick={controller.selectScheduled} sx={{ alignSelf: 'stretch' }}>
                Select all {scheduledOptions.length} scheduled medications
              </Button>
            ) : (
              <Alert severity="info">
                No medications scheduled for this time were found. Choose from the full medication list or add one.
              </Alert>
            )}

            <TextField
              size="small"
              label="Search medications"
              value={controller.search}
              onChange={(event) => controller.setSearch(event.target.value)}
              fullWidth
            />

            <Box>
              {visibleOptions.length ? visibleOptions.map((option) => (
                <FormControlLabel
                  key={option.id}
                  control={(
                    <Checkbox
                      checked={controller.selectedIds.includes(option.id)}
                      onChange={() => controller.toggleMedication(option.id)}
                    />
                  )}
                  label={getAdministrationOptionLabel(option)}
                  sx={{ display: 'flex', m: 0, py: 0.35, width: '100%' }}
                />
              )) : (
                <Typography sx={{ color: '#6B7280', fontSize: '0.84rem' }}>
                  No matching medications found.
                </Typography>
              )}
            </Box>

            <Button variant="text" onClick={controller.openAddMedication} sx={{ alignSelf: 'flex-start' }}>
              Add a medication to {childName ? `${childName}’s` : 'the'} list
            </Button>

            <Stack direction={{ xs: 'column-reverse', sm: 'row' }} spacing={0.8}>
              <Button variant="text" onClick={controller.closeReview} disabled={controller.saving} fullWidth>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={controller.confirm}
                disabled={!controller.selectedIds.length || controller.saving}
                fullWidth
              >
                {controller.saving ? 'Saving…' : 'Confirm selection'}
              </Button>
            </Stack>
          </Stack>
        </LogFormShell>
      ) : null}

      {controller.addOpen ? (
        <MedicationEditorSheet
          open
          onClose={controller.closeAddMedication}
          onClearDraft={controller.closeAddMedication}
          childName={childName}
          child={{ name: childName }}
          title="Add medication"
          draft={controller.addDraft || {}}
          onDraftChange={controller.updateAddDraft}
          onSaveDraft={controller.saveAddedMedication}
          saveStatus={controller.saving ? 'saving' : 'idle'}
          saveError={controller.error}
        />
      ) : null}
    </>
  );
};

export default MedicationAdministrationReviewSheet;
